import type { LedgerAccountType } from '@fragment/types';
import type { CurrencyMode } from '@fragment/types/currencyCodes';
import type {
  AccountConsistencyConfig,
  SchemaLedgerEntityStatus,
} from '@fragment/types/schemas';
import { SchemaLedgerEntityStatusSchema } from '@fragment/types/schemas';

import { resolveBalanceUpdatesConsistency } from './consistencyValidation';

export type PaymentAccountRule =
  | 'asset'
  | 'template'
  | 'active'
  | 'single-currency'
  | 'usd'
  | 'no-children'
  | 'strongly-consistent';

type MessageParams = { key: string; name: string | undefined };

/**
 * Error messages for the payment account rules.
 */
export const paymentAccountRuleMessages = {
  asset: ({ key, name }) =>
    `Payment Accounts must be asset accounts (key: ${key}, name: ${name})`,
  template: ({ key, name }) =>
    `Payment Accounts cannot be Template Accounts or children of Template Accounts (key: ${key}, name: ${name})`,
  active: ({ key, name }) =>
    `Payment Accounts must be active (key: ${key}, name: ${name})`,
  'single-currency': ({ key, name }) =>
    `Payment Accounts must be single-currency (key: ${key}, name: ${name})`,
  usd: ({ key, name }) =>
    `Payment Accounts must use USD (key: ${key}, name: ${name})`,
  'no-children': ({ key, name }) =>
    `Payment Accounts cannot have children (key: ${key}, name: ${name})`,
  'strongly-consistent': ({ key, name }) =>
    `Payment Accounts must be strongly consistent. Set ownBalanceUpdates or totalBalanceUpdates to 'strong' (key: ${key}, name: ${name})`,
} satisfies Record<PaymentAccountRule, (params: MessageParams) => string>;

/** At most one payment account can exist per schema. */
export const paymentAccountUniquenessMessage = ({
  key,
}: {
  key: string;
}): string => `Only one Payment Account is allowed per schema (key: ${key})`;

type BalanceUpdatesConfig = Pick<
  AccountConsistencyConfig,
  'ownBalanceUpdates' | 'totalBalanceUpdates'
>;

export type PaymentAccountRuleInput = {
  key: string;
  name: string | undefined;
  type: LedgerAccountType | undefined;
  isTemplateOrTemplateChild: boolean;
  status: SchemaLedgerEntityStatus;
  currencyMode: CurrencyMode | undefined;
  currencyCode: string | undefined;
  hasChildren: boolean;
  consistencyConfig: BalanceUpdatesConfig | undefined;
  defaultConsistencyConfig: BalanceUpdatesConfig | undefined;
};

export type PaymentAccountViolation = {
  rule: PaymentAccountRule;
  message: string;
};

/**
 * Validates an account against the payment account rules.
 */
export const validatePaymentAccountRules = (
  input: PaymentAccountRuleInput
): PaymentAccountViolation[] => {
  const { key, name } = input;
  const violations: PaymentAccountViolation[] = [];
  const addViolation = (rule: PaymentAccountRule) =>
    violations.push({
      rule,
      message: paymentAccountRuleMessages[rule]({ key, name }),
    });

  if (input.type !== 'asset') {
    addViolation('asset');
  }
  if (input.isTemplateOrTemplateChild) {
    addViolation('template');
  }
  if (input.status !== SchemaLedgerEntityStatusSchema.Values.active) {
    addViolation('active');
  }
  if (input.currencyMode === 'multi') {
    addViolation('single-currency');
  } else if (input.currencyCode !== 'USD') {
    addViolation('usd');
  }
  if (input.hasChildren) {
    addViolation('no-children');
  }
  const { ownBalanceUpdates, totalBalanceUpdates } =
    resolveBalanceUpdatesConsistency({
      accountConsistencyConfig: input.consistencyConfig,
      defaultConsistencyConfig: input.defaultConsistencyConfig,
    });
  if (ownBalanceUpdates !== 'strong' && totalBalanceUpdates !== 'strong') {
    addViolation('strongly-consistent');
  }
  return violations;
};
